import type { NavigationCategory, NavigationItem } from '@/lib/use-layout-config';
import { resolveProfileKey } from '@/pages/wms/wms-profile-config';

const ADMIN_ROLES = new Set(['platform_admin', 'admin', 'local_admin', 'solution_admin', 'group_admin', 'group_owner']);
const BUILDER_ROLES = new Set(['platform_admin', 'group_admin', 'group_owner']);
const ALWAYS_ON_MODULES = new Set(['OMS', 'INBOX', 'CRM', 'SET']);
const DEVELOPER_ONLY_ITEM_IDS = new Set([
  'settings-page-performance',
  'settings-builder',
  'settings-business-packages',
]);

const PERMISSION_ALIAS_GROUPS: ReadonlyArray<readonly string[]> = [
  ['sc.read', 'supply-chain:read'],
  ['sc.write', 'supply-chain:write'],
  ['builder:read', 'business-builder:read'],
  ['builder:write', 'business-builder:write'],
] as const;

const ROLE_DEFAULT_GRANTS: Record<string, Set<string>> = {
  manager:    new Set(['ai.read', 'crm.read', 'job:read', 'shipment:read', 'order:read', 'conversation:read', 'customer:read', 'ledger:read', 'employee:read', 'warehouse:read', 'hrss:read', 'supply-chain:read', 'pipeline:read', 'service-order:read', 'config:read', 'tm.read', 'ct.read', 'wp.read', 'wg.read', 'wm.read', 'cf.read', 'fm.read', 'acc.read']),
  warehouse:  new Set(['warehouse:read', 'conversation:read', 'hrss:read', 'wp.read', 'wg.read', 'wm.read', 'cf.read']),
  staff:      new Set(['ai.read', 'crm.read', 'warehouse:read', 'job:read', 'order:read', 'conversation:read', 'customer:read', 'hrss:read', 'supply-chain:read', 'pipeline:read', 'tm.read', 'ct.read', 'wp.read', 'wg.read', 'wm.read', 'cf.read', 'fm.read', 'acc.read']),
  supervisor: new Set(['ai.read', 'crm.read', 'warehouse:read', 'job:read', 'order:read', 'conversation:read', 'customer:read', 'employee:read', 'ledger:read', 'hrss:read', 'supply-chain:read', 'pipeline:read', 'tm.read', 'ct.read', 'wp.read', 'wg.read', 'wm.read', 'cf.read', 'fm.read', 'acc.read']),
  driver:     new Set(['job:read', 'conversation:read', 'hrss:read', 'tm.read', 'ct.read', 'wp.read', 'wg.read', 'wm.read', 'cf.read']),
  member:     new Set(['order:read', 'conversation:read', 'hrss:read', 'supply-chain:read']),
};

export type NavigationVisibilityReasonCode =
  | 'visible'
  | 'chat_only'
  | 'developer_only'
  | 'permission_denied'
  | 'platform_admin_only'
  | 'module_disabled'
  | 'wms_profile_mismatch'
  | 'empty_category';

export interface NavigationVisibilityContext {
  role?: string;
  grants?: string[];
  enabledModules?: string[];
  warehouseProfile?: string;
  isAdminConsole?: boolean;
  isTenantDeveloper?: boolean;
  isChatOnly?: boolean;
}

export interface NavigationItemVisibility {
  item: NavigationItem;
  visible: boolean;
  reason: NavigationVisibilityReasonCode;
}

export interface NavigationCategoryVisibility {
  category: NavigationCategory;
  visible: boolean;
  reason: NavigationVisibilityReasonCode;
  items: NavigationItemVisibility[];
}

export function normalizeEnabledModules(enabledModules?: string[]): Set<string> {
  const moduleSet = new Set([
    ...(enabledModules ?? []).map((moduleId) => moduleId.trim().toUpperCase()).filter(Boolean),
    ...ALWAYS_ON_MODULES,
  ]);
  if (moduleSet.has('AIC')) moduleSet.add('AIW');
  if (moduleSet.has('AIW')) moduleSet.add('AIC');
  if (moduleSet.has('WMS')) {
    moduleSet.add('WM');
    moduleSet.add('WP');
  }
  return moduleSet;
}

function canSeePermission(
  role: string,
  grants: string[],
  requiredPermission: string | undefined,
  isTenantDeveloper: boolean,
): NavigationVisibilityReasonCode {
  if (requiredPermission?.startsWith('builder:') || requiredPermission?.startsWith('business-builder:')) {
    return BUILDER_ROLES.has(role) || grants.some((grant) => permissionMatches(grant, requiredPermission))
      ? 'visible'
      : 'permission_denied';
  }
  if (requiredPermission === 'developer:technical' || requiredPermission?.startsWith('technical:')) {
    return isTenantDeveloper ? 'visible' : 'developer_only';
  }
  if (role === 'platform_admin') {
    return requiredPermission === 'platform:admin' ? 'visible' : 'platform_admin_only';
  }
  if (!requiredPermission || requiredPermission === '*') return 'visible';
  if (ADMIN_ROLES.has(role)) return requiredPermission !== 'platform:admin' ? 'visible' : 'platform_admin_only';
  if (role === 'management') {
    return requiredPermission !== 'platform:admin' && requiredPermission !== 'config:write'
      ? 'visible'
      : 'permission_denied';
  }
  if (grants.some((grant) => permissionMatches(grant, requiredPermission))) return 'visible';
  const defaults = ROLE_DEFAULT_GRANTS[role];
  return defaults && Array.from(defaults).some((grant) => permissionMatches(grant, requiredPermission))
    ? 'visible'
    : 'permission_denied';
}

function permissionAliases(permission: string): Set<string> {
  const values = new Set([permission]);
  for (const group of PERMISSION_ALIAS_GROUPS) {
    if (group.includes(permission)) {
      for (const item of group) values.add(item);
    }
  }
  return values;
}

function permissionMatches(grant: string, permission: string): boolean {
  const grantAliases = permissionAliases(grant);
  const permissionAliasesSet = permissionAliases(permission);

  for (const left of grantAliases) {
    for (const right of permissionAliasesSet) {
      if (left === right || left === '*') return true;
      const [grantDomain, grantAction] = left.split(':');
      const [domain, action] = right.split(':');
      if ((grantDomain === '*' || grantDomain === domain) && (grantAction === '*' || grantAction === action)) {
        return true;
      }
    }
  }

  return false;
}

export function resolveEffectivePermission(
  categoryId: string,
  categoryPermission: string | undefined,
  itemPermission?: string,
): string | undefined {
  if (itemPermission) return itemPermission;
  if (categoryPermission) return categoryPermission;
  if (categoryId.startsWith('cc-')) return 'platform:admin';
  return undefined;
}

function evaluateItemVisibility(
  category: NavigationCategory,
  item: NavigationItem,
  context: Required<NavigationVisibilityContext>,
  enabledModuleSet: Set<string>,
  activeProfile: string,
): NavigationItemVisibility {
  const effectivePerm = resolveEffectivePermission(category.id, category.requiredPermission, item.requiredPermission);
  if (DEVELOPER_ONLY_ITEM_IDS.has(item.id) && !context.isTenantDeveloper) {
    return { item, visible: false, reason: 'developer_only' };
  }

  const permissionReason = canSeePermission(context.role, context.grants, effectivePerm, context.isTenantDeveloper);
  if (permissionReason !== 'visible') {
    return { item, visible: false, reason: permissionReason };
  }

  if (context.isAdminConsole && effectivePerm !== 'platform:admin') {
    return { item, visible: false, reason: 'platform_admin_only' };
  }

  if (item.requiredModule && !ALWAYS_ON_MODULES.has(item.requiredModule) && !enabledModuleSet.has(item.requiredModule)) {
    return { item, visible: false, reason: 'module_disabled' };
  }

  if (item.wmsProfiles && item.wmsProfiles.length > 0 && !item.wmsProfiles.includes(activeProfile)) {
    return { item, visible: false, reason: 'wms_profile_mismatch' };
  }

  return { item, visible: true, reason: 'visible' };
}

export function evaluateNavigationVisibility(
  navigation: NavigationCategory[],
  rawContext: NavigationVisibilityContext,
): NavigationCategoryVisibility[] {
  const context: Required<NavigationVisibilityContext> = {
    role: rawContext.role ?? 'viewer',
    grants: rawContext.grants ?? [],
    enabledModules: rawContext.enabledModules ?? [],
    warehouseProfile: rawContext.warehouseProfile ?? '',
    isAdminConsole: rawContext.isAdminConsole ?? false,
    isTenantDeveloper: rawContext.isTenantDeveloper ?? false,
    isChatOnly: rawContext.isChatOnly ?? false,
  };

  if (context.isChatOnly) {
    return navigation.map((category) => ({
      category,
      visible: false,
      reason: 'chat_only',
      items: category.items.map((item) => ({ item, visible: false, reason: 'chat_only' })),
    }));
  }

  const enabledModuleSet = normalizeEnabledModules(context.enabledModules);
  const activeProfile = resolveProfileKey(context.warehouseProfile);

  return navigation.map((category) => {
    const items = category.items.map((item) => evaluateItemVisibility(category, item, context, enabledModuleSet, activeProfile));
    const visibleItems = items.filter((item) => item.visible);
    const categoryPermission = canSeePermission(
      context.role,
      context.grants,
      resolveEffectivePermission(category.id, category.requiredPermission),
      context.isTenantDeveloper,
    );

    if (categoryPermission !== 'visible') {
      return { category, visible: false, reason: categoryPermission, items };
    }

    if (category.requiredModule && !ALWAYS_ON_MODULES.has(category.requiredModule) && !enabledModuleSet.has(category.requiredModule)) {
      return { category, visible: false, reason: 'module_disabled', items };
    }

    if (context.isAdminConsole && resolveEffectivePermission(category.id, category.requiredPermission) !== 'platform:admin') {
      return { category, visible: false, reason: 'platform_admin_only', items };
    }

    if (visibleItems.length === 0 && !category.dynamicRuntime) {
      return { category, visible: false, reason: 'empty_category', items };
    }

    return { category, visible: true, reason: 'visible', items };
  });
}

export function filterVisibleNavigation(
  navigation: NavigationCategory[],
  context: NavigationVisibilityContext,
): NavigationCategory[] {
  return evaluateNavigationVisibility(navigation, context)
    .filter((entry) => entry.visible)
    .map((entry) => {
      const visibleItems = entry.items.filter((item) => item.visible).map((item) => item.item);
      return visibleItems.length === entry.category.items.length
        ? entry.category
        : { ...entry.category, items: visibleItems };
    })
    .sort((left, right) => left.order - right.order);
}
